import React from "react";
import { motion } from "framer-motion";
import { pressable } from "../motion";
import { cn } from "../../utils/classnames";

// Buttons for customer-facing popups. The look is the booking-confirmation
// popup's ("カートに追加しました"): full-width, 50px tall, rounded.
//   primary   → gold with white text (the main action: Proceed, Log Out…)
//   secondary → dark outline that fills dark on hover (Cancel, Back…)

const BASE_BUTTON_CLASSES =
  "tw:rounded-[0.375rem]! tw:inline-flex tw:items-center tw:justify-center tw:font-normal tw:leading-normal tw:text-center tw:no-underline tw:cursor-pointer tw:select-none tw:border tw:border-transparent tw:px-3 tw:text-base tw:transition-[color,background-color,border-color,box-shadow] tw:duration-150 tw:disabled:opacity-65 tw:disabled:pointer-events-none tw:w-full tw:h-[50px] tw:!font-[400]";

const VARIANT_CLASSES = {
  primary:
    "tw:bg-[#d4b055] tw:border-[#d4b055] tw:hover:bg-[#c2a86b] tw:hover:border-[#c2a86b] tw:hover:opacity-60 tw:!text-[#FFFFFF]",
  secondary: "btn-outline-282828",
};

const LoadingSpinner = () => (
  <span
    className="tw:inline-block tw:w-5 tw:h-5 tw:border-2 tw:border-current tw:border-r-transparent tw:rounded-full tw:animate-spin"
    role="status"
  >
    <span className="tw:sr-only">Loading...</span>
  </span>
);

/**
 * One popup action button.
 *
 * @param {"primary"|"secondary"} [variant="primary"]
 * @param {boolean} [isLoading] - shows a spinner and disables the button
 */
export const ModalButton = ({
  variant = "primary",
  isLoading = false,
  disabled = false,
  className,
  children,
  type = "button",
  ...buttonProps
}) => {
  const isDisabled = disabled || isLoading;

  return (
    <motion.button
      type={type}
      disabled={isDisabled}
      whileTap={isDisabled ? undefined : pressable.tap}
      className={cn(BASE_BUTTON_CLASSES, VARIANT_CLASSES[variant] || VARIANT_CLASSES.primary, className)}
      {...buttonProps}
    >
      {isLoading ? <LoadingSpinner /> : children}
    </motion.button>
  );
};

/**
 * Lays popup buttons out the same way everywhere: stacked on phones,
 * side by side from tablet width up. Put the secondary button first.
 */
export const ModalButtonRow = ({ className, children }) => (
  <div className={cn("tw:flex tw:flex-col tw:gap-3 tw:md:flex-row tw:md:gap-2 tw:justify-center", className)}>
    {children}
  </div>
);
